import { Link } from 'react-router-dom';

const features = [
  {
    icon: 'ri-quill-pen-ai-line',
    title: 'AI-Generated Ad Copy That Converts',
    description:
      'Our AI ad generator writes hooks, headlines, body copy, and CTAs tailored to your business, audience, and platform. Every word is optimized for clicks and conversions — no copywriting skills required.',
    highlight: 'Writes like a $200/hr copywriter',
    color: 'orange',
    image:
      'https://readdy.ai/api/search-image?query=close%20up%20of%20a%20laptop%20screen%20showing%20well-written%20marketing%20ad%20copy%20text%20with%20highlighted%20headlines%20and%20call-to-action%20buttons%2C%20warm%20amber%20lighting%2C%20clean%20minimal%20workspace%2C%20sharp%20focus%20on%20screen%2C%20professional%20atmosphere&width=600&height=380&seq=feat-copy-01&orientation=landscape',
  },
  {
    icon: 'ri-image-line',
    title: 'AI-Generated Ad Visuals in Seconds',
    description:
      'Stop paying for stock photos. Our AI generates custom ad images that match your brand, products, and messaging — from Facebook banners to Instagram squares to Google Display ads.',
    highlight: 'No designer needed',
    color: 'amber',
    image:
      'https://readdy.ai/api/search-image?query=vibrant%20social%20media%20ad%20creative%20displayed%20on%20a%20phone%20screen%20with%20colorful%20product%20imagery%2C%20bright%20studio%20lighting%2C%20clean%20modern%20composition%2C%20advertising%20design%20aesthetic%2C%20rich%20saturated%20colors%2C%20sharp%20details&width=600&height=380&seq=feat-image-01&orientation=landscape',
  },
  {
    icon: 'ri-flashlight-line',
    title: 'Launch-Ready Ads in Under 3 Minutes',
    description:
      'Go from blank page to live ad faster than ever. Describe your business, pick your platform, and your complete AI-generated ad — copy and creative — is ready to launch. No learning curve, no waiting.',
    highlight: 'Save 6+ hours per week',
    color: 'rose',
    image:
      'https://readdy.ai/api/search-image?query=entrepreneur%20at%20a%20modern%20desk%20looking%20satisfied%20at%20multiple%20social%20media%20ads%20displayed%20on%20dual%20monitors%2C%20warm%20cozy%20office%20environment%2C%20golden%20hour%20lighting%20streaming%20through%20window%2C%20productive%20and%20successful%20atmosphere&width=600&height=380&seq=feat-fast-01&orientation=landscape',
  },
  {
    icon: 'ri-bar-chart-2-line',
    title: 'Multi-Platform Ad Formats',
    description:
      'Generate ads for Facebook, Instagram, Google, TikTok, and LinkedIn — all properly sized and formatted for each platform. One input, every format covered.',
    highlight: '5 platforms supported',
    color: 'teal',
    image:
      'https://readdy.ai/api/search-image?query=multiple%20social%20media%20platform%20interfaces%20shown%20on%20different%20devices%20including%20phone%20tablet%20and%20desktop%2C%20showing%20facebook%20instagram%20google%20ads%2C%20clean%20modern%20flat%20layout%2C%20light%20minimal%20background%2C%20digital%20marketing%20concept&width=600&height=380&seq=feat-platform-01&orientation=landscape',
  },
  {
    icon: 'ri-translate-2',
    title: 'A/B Variations Instantly',
    description:
      'Test more, win more. Generate 5, 10, or 20 ad variations from a single prompt. Different angles, different hooks, different audiences — let data decide which ad wins.',
    highlight: 'Up to 20 variations at once',
    color: 'violet',
    image:
      'https://readdy.ai/api/search-image?query=split%20screen%20A%20B%20testing%20comparison%20of%20two%20different%20advertising%20creatives%20on%20a%20screen%2C%20data%20charts%20showing%20performance%20metrics%2C%20clean%20analytical%20dashboard%20interface%2C%20professional%20tech%20environment%2C%20cool%20neutral%20tones&width=600&height=380&seq=feat-ab-01&orientation=landscape',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Built for Beginners, Loved by Pros',
    description:
      'No jargon, no confusing settings. Whether you have zero marketing experience or run a full agency, our AI ad generator adapts to your skill level and scales with your ambition.',
    highlight: 'No experience needed',
    color: 'emerald',
    image:
      'https://readdy.ai/api/search-image?query=diverse%20group%20of%20small%20business%20owners%20looking%20happily%20at%20a%20tablet%20showing%20advertising%20results%2C%20collaborative%20friendly%20environment%2C%20warm%20natural%20office%20lighting%2C%20authentic%20candid%20moment%2C%20positive%20successful%20team%20atmosphere&width=600&height=380&seq=feat-beginner-01&orientation=landscape',
  },
];

const colorMap: Record<string, { bg: string; text: string; badge: string }> = {
  orange: { bg: 'bg-orange-50', text: 'text-orange-600', badge: 'bg-orange-100 text-orange-700' },
  amber: { bg: 'bg-amber-50', text: 'text-amber-600', badge: 'bg-amber-100 text-amber-700' },
  rose: { bg: 'bg-rose-50', text: 'text-rose-600', badge: 'bg-rose-100 text-rose-700' },
  teal: { bg: 'bg-teal-50', text: 'text-teal-600', badge: 'bg-teal-100 text-teal-700' },
  violet: { bg: 'bg-violet-50', text: 'text-violet-600', badge: 'bg-violet-100 text-violet-700' },
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600', badge: 'bg-emerald-100 text-emerald-700' },
};

export default function LandingFeatures() {
  return (
    <section id="features" className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-xs font-bold uppercase tracking-widest text-orange-500 mb-3 block">
            Why Choose AdForge
          </span>
          <h2 className="text-3xl lg:text-4xl font-black text-stone-900 mb-4 leading-tight">
            Everything You Need to Run Better Ads with AI
          </h2>
          <p className="text-base text-stone-500 leading-relaxed">
            The best AI ad generator doesn&apos;t just write words — it creates complete, launch-ready
            ad campaigns. Here&apos;s what makes AdForge different.
          </p>
        </div>

        {/* Feature grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feat) => {
            const c = colorMap[feat.color];
            return (
              <article
                key={feat.title}
                className="rounded-2xl border border-stone-100 bg-white overflow-hidden hover:-translate-y-1 transition-transform duration-300 cursor-default"
              >
                {/* Image */}
                <div className="w-full h-44 overflow-hidden">
                  <img
                    src={feat.image}
                    alt={feat.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                {/* Body */}
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${c.bg}`}>
                      <i className={`${feat.icon} text-lg ${c.text}`} />
                    </div>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${c.badge}`}>
                      {feat.highlight}
                    </span>
                  </div>
                  <h3 className="text-base font-bold text-stone-900 mb-2 leading-snug">
                    {feat.title}
                  </h3>
                  <p className="text-sm text-stone-500 leading-relaxed">{feat.description}</p>
                </div>
              </article>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center mt-14">
          <Link
            to="/signup"
            className="inline-flex items-center gap-2 px-8 py-4 bg-orange-500 hover:bg-orange-600 text-white font-bold text-base rounded-xl transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            Start Generating Ads Free
            <i className="ri-arrow-right-line" />
          </Link>
          <p className="text-xs text-stone-400 mt-3">No credit card required &middot; 10 free ads included</p>
        </div>
      </div>
    </section>
  );
}
